import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, ArrowUpRight, ShieldCheck, BadgeCheck, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const SLIDES = [
  {
    id: 1,
    heading: "Powerful Upgrades and Connected Services for your Growing Business",
    description:
      "Stay ahead with the latest Tally upgrades, connected services, and expert support designed to scale your business operations effortlessly.",
    offer: "Renew anytime before your expiry and get 1 month free TSS",
    cta: "Renew Now",
    image: "/hero-1.png",
    imageLabel: "Tally Software Services",
  },
  {
    id: 2,
    heading: "Tally Software Services",
    description:
      "Get unlimited access to the latest product upgrades, statutory updates, and priority support to keep your business always compliant.",
    offer: "Annual TSS plans starting with exclusive partner discounts",
    cta: "Explore TSS",
    image: "/hero-tss.png",
    imageLabel: "TSS Benefits",
  },
  {
    id: 3,
    heading: "TallyPrime Cloud Access",
    description:
      "Access your TallyPrime data securely from anywhere, anytime. Work remotely with real-time collaboration and bank-grade security.",
    offer: "Start your cloud journey with flexible monthly plans",
    cta: "Go Cloud",
    image: "/hero-cloud.png",
    imageLabel: "Cloud Access",
  },
  {
    id: 4,
    heading: "TallyPrime with Whatsapp",
    description:
      "Send invoices, payment reminders, and business reports directly to your customers via WhatsApp from within TallyPrime.",
    offer: "Integrate WhatsApp messaging and improve customer engagement",
    cta: "Learn More",
    image: "/hero-whatsapp.png",
    imageLabel: "WhatsApp Integration",
  },
  {
    id: 5,
    heading: "TallyDrive",
    description:
      "Store, share, and manage your business documents securely in the cloud with seamless TallyPrime integration.",
    offer: "Cloud storage built for your Tally workflows",
    cta: "Discover TallyDrive",
    image: "/hero-server.png",
    imageLabel: "TallyDrive Storage",
  },
  {
    id: 6,
    heading: "TallyIra",
    description:
      "Leverage intelligent analytics and actionable insights with TallyIra to make faster, data-driven business decisions.",
    offer: "AI-powered reports and dashboards for modern businesses",
    cta: "Try TallyIra",
    image: "/hero-tallyira.png",
    imageLabel: "TallyIra Analytics",
  },
  {
    id: 7,
    heading: "TallyPrime Server",
    description:
      "Enable high-performance multi-user operations with TallyPrime Server for faster, concurrent, and secure data access.",
    offer: "Scale your multi-user Tally deployment with enterprise reliability",
    cta: "Get Server",
    image: "/hero-server.png",
    imageLabel: "TallyPrime Server",
  },
];

const AUTOPLAY_MS = 5000;

const scrollTo = (id) => {
  const el = document.querySelector(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Hero() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (paused) return;
    timerRef.current = setTimeout(() => {
      setIndex((i) => (i + 1) % SLIDES.length);
    }, AUTOPLAY_MS);
    return () => clearTimeout(timerRef.current);
  }, [index, paused]);

  const goPrev = () => setIndex((i) => (i - 1 + SLIDES.length) % SLIDES.length);
  const goNext = () => setIndex((i) => (i + 1) % SLIDES.length);

  const slide = SLIDES[index];

  return (
    <section
      id="home"
      data-testid="hero-section"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#FFF8E7] text-[#1A1A1A]"
    >
      {/* Decorative wave background */}
      <div className="absolute inset-0 pointer-events-none">
        <svg
          className="absolute bottom-0 left-0 w-full h-auto"
          viewBox="0 0 1440 320"
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill="#F4B942"
            fillOpacity="0.25"
            d="M0,224L48,213.3C96,203,192,181,288,181.3C384,181,480,203,576,224C672,245,768,267,864,250.7C960,235,1056,181,1152,165.3C1248,149,1344,171,1392,181.3L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
          />
        </svg>
        <svg
          className="absolute bottom-0 left-0 w-full h-auto"
          viewBox="0 0 1440 320"
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill="#FFFFFF"
            fillOpacity="0.6"
            d="M0,256L48,245.3C96,235,192,213,288,213.3C384,213,480,235,576,245.3C672,256,768,256,864,240C960,224,1056,192,1152,186.7C1248,181,1344,203,1392,213.3L1440,224L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
          />
        </svg>
      </div>

      {/* Top partner badge */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-28 lg:pt-32">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2 rounded-full bg-[#0D47A1]/10 border border-[#0D47A1]/20 px-4 py-2 mb-6"
        >
          <BadgeCheck className="w-4 h-4 text-[#0D47A1]" />
          <span className="text-xs font-semibold tracking-[0.14em] uppercase text-[#0D47A1]">
            Authorized Tally certified Partner
          </span>
        </motion.div>
      </div>

      {/* Slider content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-4 pb-32 min-h-[calc(100svh-120px)] flex items-center">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center w-full">
          {/* Left content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={slide.id}
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 30 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-xl"
            >
              <h1 className="font-display font-black text-[#1A1A1A] leading-[1.08] tracking-[-0.02em] text-[34px] sm:text-[42px] lg:text-[52px]">
                {slide.heading}
              </h1>

              <p className="mt-5 text-base lg:text-lg text-[#4A5568] leading-relaxed font-body max-w-md">
                {slide.description}
              </p>

              <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <Button
                  onClick={() => scrollTo("#contact")}
                  data-testid="hero-demo-button"
                  className="btn-magnetic rounded-lg bg-[#F4B942] hover:bg-[#E6A832] text-[#1A1A1A] h-13 px-7 text-[15px] font-bold shadow-[0_12px_35px_rgba(244,185,66,0.45)]"
                >
                  {slide.cta}
                  <ArrowUpRight className="w-4 h-4 ml-2" />
                </Button>
              </div>

              {/* Blue offer banner */}
              <div className="mt-6 inline-flex items-center bg-[#0D47A1] text-white px-5 py-3 rounded-r-xl rounded-l-sm shadow-lg transform -skew-x-6 origin-left">
                <span className="transform skew-x-6 text-sm font-semibold">
                  {slide.offer}
                </span>
              </div>

              {/* Trust bar */}
              <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-[#4A5568]">
                <div className="flex items-center gap-2 text-sm">
                  <ShieldCheck className="w-4 h-4 text-[#0D47A1]" />
                  20+ Years Experience
                </div>
                <div className="h-4 w-px bg-[#0D47A1]/20 hidden sm:block" />
                <div className="flex items-center gap-2 text-sm">
                  <ShieldCheck className="w-4 h-4 text-[#0D47A1]" />
                  1500+ Happy Clients
                </div>
                <div className="h-4 w-px bg-[#0D47A1]/20 hidden sm:block" />
                <div className="flex items-center gap-2 text-sm">
                  <ShieldCheck className="w-4 h-4 text-[#0D47A1]" />
                  24x7 Support
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Right - Image placeholder */}
          <AnimatePresence mode="wait">
            <motion.div
              key={slide.id}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="relative flex items-center justify-center"
            >
              <div className="relative w-full max-w-[540px] aspect-[4/3] rounded-3xl overflow-hidden bg-white shadow-[0_30px_80px_rgba(13,71,161,0.12)] border border-[#0D47A1]/10">
                <img
                  src={slide.image}
                  alt={slide.imageLabel}
                  className="w-full h-full object-cover"
                  loading={index === 0 ? "eager" : "lazy"}
                />
              </div>

              {/* Floating stat card */}
              <div className="absolute -bottom-5 -left-4 sm:-left-6 bg-white rounded-2xl p-4 shadow-[0_20px_60px_rgba(13,71,161,0.15)] border border-[#0D47A1]/5">
                <div className="flex items-baseline gap-2">
                  <span className="font-display font-black text-3xl text-[#0D47A1] tracking-tight">1500+</span>
                  <span className="text-sm text-[#4A5568]">Businesses</span>
                </div>
                <p className="mt-1 text-xs text-[#4A5568]">Trusted across India</p>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Video Popup Modal */}
      <AnimatePresence>
        {videoOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[999] flex items-center justify-center bg-black/80 backdrop-blur-sm px-4"
            onClick={() => setVideoOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.85, opacity: 0 }}
              transition={{ type: "spring", stiffness: 280, damping: 22 }}
              className="relative w-full max-w-4xl rounded-2xl overflow-hidden shadow-[0_40px_100px_rgba(0,0,0,0.7)] bg-black"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setVideoOpen(false)}
                className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/10 hover:bg-white/25 border border-white/20 flex items-center justify-center transition"
              >
                <X className="w-4 h-4 text-white" />
              </button>
              <video
                src="/gallery/prime-technology-awards-video.mp4"
                controls
                autoPlay
                className="w-full max-h-[80vh] object-contain"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Slider controls */}
      <div className="absolute inset-x-0 bottom-8 z-10 max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between">
        {/* Dots */}
        <div className="flex items-center gap-2" data-testid="hero-slider-dots">
          {SLIDES.map((s, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              data-testid={`hero-slider-dot-${i}`}
              aria-label={`Go to slide ${i + 1}`}
              className="group relative h-2 overflow-hidden rounded-full transition-all duration-500"
              style={{
                width: i === index ? 40 : 14,
                background: i === index ? "#F4B942" : "rgba(13,71,161,0.25)",
              }}
              title={s.heading}
            />
          ))}
        </div>

        {/* Arrows + label */}
        <div className="hidden sm:flex items-center gap-3">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4A5568]">
            {String(index + 1).padStart(2, "0")} / {String(SLIDES.length).padStart(2, "0")} · {slide.imageLabel}
          </span>
          <button
            onClick={goPrev}
            data-testid="hero-slider-prev"
            aria-label="Previous slide"
            className="w-11 h-11 rounded-lg bg-white border border-[#0D47A1]/15 flex items-center justify-center hover:bg-[#0D47A1] hover:text-white text-[#0D47A1] transition shadow-sm"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={goNext}
            data-testid="hero-slider-next"
            aria-label="Next slide"
            className="w-11 h-11 rounded-lg bg-white border border-[#0D47A1]/15 flex items-center justify-center hover:bg-[#0D47A1] hover:text-white text-[#0D47A1] transition shadow-sm"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
}
